import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {PorQue} from '@/components/ui/por-que'

type ConfirmDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Una línea, ≤ 20 palabras (T5, `docs/doctrina/ux-texto-spec.md`); el detalle va en `porque`. */
  description: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  pending?: boolean
  /** Lo que se confirma aún no está a la vista: el botón espera. */
  disabled?: boolean
  /** Se despliega en flujo, no en popover: un popover tapaba el footer, que arranca a 16 px del disparador. */
  porque?: {label: string; titulo?: string; contenido: ReactNode; testId?: string; contenidoTestId?: string}
  /** Dentro del diálogo: sigue abierto hasta que el acto sale bien y un error detrás no se ve (KAI-334). */
  error?: string | null
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  pending,
  disabled,
  porque,
  error,
}: ConfirmDialogProps) {
  const {t} = useTranslation()
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {porque && (
          <PorQue
            label={porque.label}
            titulo={porque.titulo}
            testId={porque.testId}
            contenidoTestId={porque.contenidoTestId}
            enFlujo>
            {porque.contenido}
          </PorQue>
        )}
        {error && (
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="confirm-dialog-error">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || disabled}
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}>
            {pending ? t('common.saving') : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
